import {
  modifierPriceKey,
  type BusinessPolicySnapshot,
  type AvailabilityStatus,
  type ModifierGroupRule,
} from '../policy/business-policy.ts';
import type { OrderLineSnapshot } from '../order/product-selection.ts';
import type { OrderFinalizationEvidence, PricingAdjustment, PricingLineEvidence } from '../order/order-finalization.ts';
import { resolveComboMatches, type ComboMatch, type PricingUnit } from './combo.ts';

export interface AvailabilityBlocker {
  readonly lineId: string;
  readonly productId: string;
  readonly node: string;
  readonly status: AvailabilityStatus | 'unknown';
}

export interface QuoteBlocked {
  readonly kind: 'blocked';
  readonly blockers: readonly AvailabilityBlocker[];
}

export type ModifierViolationCode =
  | 'modifier_tier_mismatch'
  | 'modifier_exclusive_option'
  | 'modifier_forbidden_pair';

export interface ModifierViolation {
  readonly code: ModifierViolationCode;
  readonly lineId: string;
  readonly productId: string;
  readonly optionGroupId: string;
  readonly ruleId: string;
  readonly optionIds: readonly string[];
}

export interface QuoteRejected {
  readonly kind: 'rejected';
  readonly violations: readonly ModifierViolation[];
}

export interface QuoteUnbound {
  readonly kind: 'unbound';
  readonly missingProductIds: readonly string[];
  readonly missingAvailabilityProductIds: readonly string[];
  readonly missingModifierPriceKeys?: readonly string[];
  readonly missingModifierTierKeys?: readonly string[];
}

export interface QuoteSuccess {
  readonly kind: 'quoted';
  readonly policyVersion: string;
  readonly comboMatches: readonly Pick<ComboMatch, 'ruleId' | 'consumedUnits'>[];
  readonly evidence: OrderFinalizationEvidence;
}

export type QuoteResult = QuoteBlocked | QuoteRejected | QuoteUnbound | QuoteSuccess;

interface ModifierResolution {
  readonly amountMinor: number;
  readonly missingPriceKeys: readonly string[];
  readonly missingTierKeys: readonly string[];
  readonly violations: readonly ModifierViolation[];
}

const availabilityForLine = (
  line: OrderLineSnapshot,
  policy: BusinessPolicySnapshot,
):
  | { readonly status: 'available'; readonly blockers: readonly AvailabilityBlocker[] }
  | { readonly status: 'blocked'; readonly blockers: readonly AvailabilityBlocker[] }
  | { readonly status: 'unbound'; readonly blockers: readonly AvailabilityBlocker[] } => {
  const requirements = policy.availability.productRequirements[line.productId];
  if (!requirements) return { status: 'unbound', blockers: [] };
  const blockers: AvailabilityBlocker[] = [];
  for (const node of requirements) {
    const status = policy.availability.nodes[node];
    if (status === undefined) blockers.push({ lineId: line.lineId, productId: line.productId, node, status: 'unknown' });
    else if (status !== 'available') blockers.push({ lineId: line.lineId, productId: line.productId, node, status });
  }
  return { status: blockers.length === 0 ? 'available' : 'blocked', blockers };
};

const validateModifierRule = (rule: ModifierGroupRule): void => {
  if (!rule.ruleId || !rule.productId || !rule.optionGroupId) {
    throw new Error(`MODIFIER_RULE_INVALID:${rule.ruleId || 'unknown'}`);
  }
  if (rule.additionalSelectionSurchargeMinor !== undefined &&
      (!Number.isSafeInteger(rule.additionalSelectionSurchargeMinor) || rule.additionalSelectionSurchargeMinor < 0)) {
    throw new Error(`MODIFIER_RULE_INVALID:${rule.ruleId}`);
  }
  for (const optionId of rule.exclusiveOptionIds ?? []) {
    if (!optionId) throw new Error(`MODIFIER_RULE_INVALID:${rule.ruleId}`);
  }
  for (const pair of rule.forbiddenPairs ?? []) {
    if (!pair.leftOptionId || !pair.rightOptionId || pair.leftOptionId === pair.rightOptionId) {
      throw new Error(`MODIFIER_RULE_INVALID:${rule.ruleId}`);
    }
  }
};

const validatePolicyIdentity = (policy: BusinessPolicySnapshot): void => {
  if (!policy.policyVersion || !policy.menuRevision || !policy.pricingRevision ||
      !policy.availabilityRevision || !policy.rulesRevision || !policy.currency) {
    throw new Error('BUSINESS_POLICY_IDENTITY_REQUIRED');
  }

  const enabledRuleKeys = new Set<string>();
  for (const rule of policy.modifierRules) {
    validateModifierRule(rule);
    if (!rule.enabled) continue;
    const key = `${rule.productId}::${rule.optionGroupId}`;
    if (enabledRuleKeys.has(key)) throw new Error(`MODIFIER_RULE_DUPLICATE:${key}`);
    enabledRuleKeys.add(key);
  }
};

const selectedByGroup = (line: OrderLineSnapshot): ReadonlyMap<string, readonly OrderLineSnapshot['productSnapshot']['selectedOptions'][number][]> => {
  const groups = new Map<string, OrderLineSnapshot['productSnapshot']['selectedOptions'][number][]>();
  for (const option of line.productSnapshot.selectedOptions) {
    const current = groups.get(option.optionGroupId) ?? [];
    groups.set(option.optionGroupId, [...current, option]);
  }
  return groups;
};

const pairMatches = (
  selectedIds: ReadonlySet<string>,
  leftOptionId: string,
  rightOptionId: string,
): boolean => selectedIds.has(leftOptionId) && selectedIds.has(rightOptionId);

const resolveModifierForLine = (
  line: OrderLineSnapshot,
  policy: BusinessPolicySnapshot,
): ModifierResolution => {
  const missingPriceKeys = new Set<string>();
  const missingTierKeys = new Set<string>();
  const violations: ModifierViolation[] = [];
  let amountMinor = 0;

  for (const option of line.productSnapshot.selectedOptions) {
    const key = modifierPriceKey(line.productId, option.optionGroupId, option.optionId);
    const authoritativeDelta = policy.pricing.modifierOptionMinor[key];
    if (typeof authoritativeDelta !== 'number' || !Number.isSafeInteger(authoritativeDelta)) {
      missingPriceKeys.add(key);
      continue;
    }
    amountMinor += authoritativeDelta;
  }

  const groups = selectedByGroup(line);
  for (const rule of policy.modifierRules) {
    if (!rule.enabled || rule.productId !== line.productId) continue;
    const selected = groups.get(rule.optionGroupId) ?? [];
    if (selected.length <= 1) continue;

    const optionIds = selected.map((option) => option.optionId);
    const selectedIds = new Set(optionIds);

    if ((rule.exclusiveOptionIds ?? []).some((optionId) => selectedIds.has(optionId))) {
      violations.push({
        code: 'modifier_exclusive_option',
        lineId: line.lineId,
        productId: line.productId,
        optionGroupId: rule.optionGroupId,
        ruleId: rule.ruleId,
        optionIds,
      });
      continue;
    }

    if ((rule.forbiddenPairs ?? []).some((pair) => pairMatches(selectedIds, pair.leftOptionId, pair.rightOptionId))) {
      violations.push({
        code: 'modifier_forbidden_pair',
        lineId: line.lineId,
        productId: line.productId,
        optionGroupId: rule.optionGroupId,
        ruleId: rule.ruleId,
        optionIds,
      });
      continue;
    }

    if (rule.requireSameTierWhenMultiple) {
      const tiers = new Set<string>();
      for (const optionId of optionIds) {
        const tier = rule.optionTiers?.[optionId];
        if (!tier?.trim()) {
          missingTierKeys.add(`${rule.ruleId}::${optionId}`);
          continue;
        }
        tiers.add(tier);
      }
      if (tiers.size > 1) {
        violations.push({
          code: 'modifier_tier_mismatch',
          lineId: line.lineId,
          productId: line.productId,
          optionGroupId: rule.optionGroupId,
          ruleId: rule.ruleId,
          optionIds,
        });
        continue;
      }
    }

    const surchargeMinor = rule.additionalSelectionSurchargeMinor ?? 0;
    amountMinor += surchargeMinor * (selected.length - 1);
  }

  if (!Number.isSafeInteger(amountMinor)) throw new Error(`PRICING_MODIFIER_TOTAL_INVALID:${line.lineId}`);
  return {
    amountMinor,
    missingPriceKeys: [...missingPriceKeys].sort(),
    missingTierKeys: [...missingTierKeys].sort(),
    violations,
  };
};

export function quoteForCommit(
  lines: readonly OrderLineSnapshot[],
  policy: BusinessPolicySnapshot,
): QuoteResult {
  validatePolicyIdentity(policy);
  if (lines.length === 0) throw new Error('PRICING_LINES_REQUIRED');

  const missingProductIds = new Set<string>();
  const missingAvailabilityProductIds = new Set<string>();
  const missingModifierPriceKeys = new Set<string>();
  const missingModifierTierKeys = new Set<string>();
  const blockers: AvailabilityBlocker[] = [];
  const violations: ModifierViolation[] = [];
  const pricingLines: PricingLineEvidence[] = [];
  const units: PricingUnit[] = [];

  for (const line of lines) {
    const baseMinor = policy.pricing.productBaseMinor[line.productId];
    if (!Number.isSafeInteger(baseMinor) || (baseMinor ?? -1) < 0) {
      missingProductIds.add(line.productId);
    }

    const availability = availabilityForLine(line, policy);
    if (availability.status === 'unbound') missingAvailabilityProductIds.add(line.productId);
    else blockers.push(...availability.blockers);

    const modifier = resolveModifierForLine(line, policy);
    modifier.missingPriceKeys.forEach((key) => missingModifierPriceKeys.add(key));
    modifier.missingTierKeys.forEach((key) => missingModifierTierKeys.add(key));
    violations.push(...modifier.violations);

    if (baseMinor === undefined || !Number.isSafeInteger(baseMinor) || baseMinor < 0 ||
        modifier.missingPriceKeys.length > 0 || modifier.missingTierKeys.length > 0 || modifier.violations.length > 0) {
      continue;
    }

    const unitModifierMinor = modifier.amountMinor;
    if (!Number.isSafeInteger(unitModifierMinor) || baseMinor + unitModifierMinor < 0) {
      throw new Error(`PRICING_UNIT_INVALID:${line.lineId}`);
    }
    const lineSubtotalMinor = (baseMinor + unitModifierMinor) * line.quantity;
    if (!Number.isSafeInteger(lineSubtotalMinor) || lineSubtotalMinor < 0) {
      throw new Error(`PRICING_LINE_TOTAL_INVALID:${line.lineId}`);
    }
    pricingLines.push({
      lineId: line.lineId,
      productId: line.productId,
      quantity: line.quantity,
      unitBaseMinor: baseMinor,
      unitModifierMinor,
      lineSubtotalMinor,
    });
    for (let unitIndex = 0; unitIndex < line.quantity; unitIndex += 1) {
      units.push({ lineId: line.lineId, unitIndex, productId: line.productId, selectorKeys: line.selectorKeys, baseMinor });
    }
  }

  if (missingProductIds.size > 0 || missingAvailabilityProductIds.size > 0 ||
      missingModifierPriceKeys.size > 0 || missingModifierTierKeys.size > 0) {
    return {
      kind: 'unbound',
      missingProductIds: [...missingProductIds].sort(),
      missingAvailabilityProductIds: [...missingAvailabilityProductIds].sort(),
      ...(missingModifierPriceKeys.size > 0
        ? { missingModifierPriceKeys: [...missingModifierPriceKeys].sort() }
        : {}),
      ...(missingModifierTierKeys.size > 0
        ? { missingModifierTierKeys: [...missingModifierTierKeys].sort() }
        : {}),
    };
  }
  if (blockers.length > 0) return { kind: 'blocked', blockers };
  if (violations.length > 0) return { kind: 'rejected', violations };

  const comboMatches = resolveComboMatches(units, policy.comboRules);
  const adjustments: PricingAdjustment[] = comboMatches
    .map((match): PricingAdjustment => ({
      kind: 'combo',
      referenceId: match.ruleId,
      amountMinor: match.fixedBaseTotalMinor - match.originalBaseTotalMinor,
      consumedUnits: match.consumedUnits.map((unit) => ({ ...unit })),
    }))
    .filter((adjustment) => adjustment.amountMinor !== 0);

  const totalMinor =
    pricingLines.reduce((sum, line) => sum + line.lineSubtotalMinor, 0) +
    adjustments.reduce((sum, adjustment) => sum + adjustment.amountMinor, 0);
  if (!Number.isSafeInteger(totalMinor) || totalMinor < 0) throw new Error('PRICING_TOTAL_INVALID');

  const uniqueProductIds = [...new Set(lines.map((line) => line.productId))];
  return {
    kind: 'quoted',
    policyVersion: policy.policyVersion,
    comboMatches: comboMatches.map((match) => ({
      ruleId: match.ruleId,
      consumedUnits: match.consumedUnits.map((unit) => ({ ...unit })),
    })),
    evidence: {
      policyVersion: policy.policyVersion,
      validation: {
        status: 'valid',
        menuRevision: policy.menuRevision,
        availabilityRevision: policy.availabilityRevision,
        rulesRevision: policy.rulesRevision,
        availabilityLines: uniqueProductIds.map((productId) => ({ productId, status: 'available' as const })),
      },
      pricing: {
        status: 'quoted',
        pricingRevision: policy.pricingRevision,
        currency: policy.currency,
        totalMinor,
        lines: pricingLines,
        adjustments,
      },
    },
  };
}
